import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';

// 대시보드 "우리 교회 이용 정보" - 요금제 성도 규모 대비 성도 앱 사용 기기 수, 상위 요금제 변경 예정.
// 서버: routers/appAdmin/usage/churchUsage.js (/church-usage)

type Level = 'normal' | 'warning' | 'over' | 'unlimited' | 'unknown';

type ChurchUsage = {
  isSuper: boolean;
  registered: boolean;
  memberSize: string | null;
  memberLimit: number | null;
  activeDevices: number;
  rollMembers: number;
  level: Level;
  limitExempt: 0 | 1;
  history: { month: string; activeDevices: number; memberLimit: number | null }[];
  plan: {
    monthlyFee: number | null;
    apps: string[] | null;
    nextBillingDate: string | null;
    lastPaymentStatus: string | null;
    autoRenew: number;
  } | null;
  pending: {
    status: 'scheduled' | 'objected';
    memberSize: string | null;
    monthlyFee: number | null;
    effectiveDate: string | null;
  } | null;
  rules: { activeWindowDays: number; overRatio: number; overMonths: number; noticeDays: number };
};

const LEVEL_BADGE: Record<Level, { label: string; className: string }> = {
  normal: { label: '여유', className: 'manage-badge--green' },
  warning: { label: '한도 임박', className: 'manage-badge--amber' },
  over: { label: '한도 초과', className: 'manage-badge--red' },
  unlimited: { label: '한도 없음', className: 'manage-badge--green' },
  unknown: { label: '규모 미설정', className: 'manage-badge--gray' },
};

const BAR_COLOR: Record<Level, string> = {
  normal: 'var(--success)',
  warning: 'var(--warn)',
  over: 'var(--danger)',
  unlimited: 'var(--success)',
  unknown: 'var(--text-muted)',
};

function won(value: number | null | undefined) {
  return value != null ? `${value.toLocaleString()}원` : '-';
}

function koreanDate(value: string | null) {
  if (!value) return '-';
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  return `${y}년 ${m}월 ${d}일`;
}

export default function ChurchUsageCard({ authChurch, requesterId }: { authChurch: string; requesterId: number }) {
  const [data, setData] = useState<ChurchUsage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [objectionOpen, setObjectionOpen] = useState(false);
  const [objectionNote, setObjectionNote] = useState('');
  const [objectionMessage, setObjectionMessage] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    setError(null);
    axios
      .get(`${MainURL}/church-usage`, { params: { authChurch, requesterId } })
      .then((res) => setData(res.data?.data ?? null))
      .catch((err) => {
        console.error('GET /church-usage', err);
        setError('이용 정보를 불러오지 못했습니다.');
      });
  }, [authChurch, requesterId]);

  useEffect(() => {
    load();
  }, [load]);

  const sendObjection = async () => {
    if (!objectionNote.trim()) {
      setObjectionMessage('사유를 입력해 주세요.');
      return;
    }
    setSending(true);
    setObjectionMessage('');
    try {
      const res = await axios.post(`${MainURL}/church-usage/objection`, { authChurch, requesterId, note: objectionNote });
      if (!res.data?.ok) throw new Error(res.data?.message);
      setObjectionOpen(false);
      setObjectionNote('');
      load();
    } catch (err) {
      const message = axios.isAxiosError(err) ? err.response?.data?.message : null;
      setObjectionMessage(message || '요청을 보내지 못했습니다.');
    } finally {
      setSending(false);
    }
  };

  if (error) return <div className="manage-state manage-state--error">{error}</div>;
  if (!data || !data.registered) return null;

  const { rules } = data;
  const ratio = data.memberLimit ? data.activeDevices / data.memberLimit : null;
  const badge = LEVEL_BADGE[data.level];
  const historyMax = Math.max(1, ...data.history.map((h) => Math.max(h.activeDevices, h.memberLimit ?? 0)));

  return (
    <section className="manage-card-block" style={{ marginBottom: 20 }}>
      <div className="manage-block-head">
        <h3 className="manage-block-title">우리 교회 이용 정보</h3>
        <span className={`manage-badge ${badge.className}`}>{badge.label}</span>
      </div>
      <div style={{ padding: 18, display: 'grid', gap: 16 }}>
        {data.pending && (
          <div
            className="manage-tip"
            style={{
              background: data.pending.status === 'objected' ? 'var(--info-bg)' : 'var(--warn-bg)',
              padding: 14,
              borderRadius: 8,
              display: 'block',
            }}
          >
            {data.pending.status === 'scheduled' ? (
              <>
                <strong>요금제 변경 예정</strong> — 최근 {rules.overMonths}개월 동안 사용 인원이 현재 요금제 한도의{' '}
                {Math.round(rules.overRatio * 100)}%를 넘어, <strong>{koreanDate(data.pending.effectiveDate)}</strong> 결제부터{' '}
                <strong>{data.pending.memberSize}</strong> 요금제
                {data.pending.monthlyFee != null && ` (월 ${won(data.pending.monthlyFee)})`}로 바뀝니다. 적용일 전에 사용 인원이 한도
                이하로 줄면 자동으로 취소됩니다.
                {data.isSuper && (
                  <div style={{ marginTop: 10 }}>
                    <button type="button" className="manage-btn" onClick={() => setObjectionOpen(true)}>
                      변경에 이의 있음
                    </button>
                  </div>
                )}
              </>
            ) : (
              <>
                <strong>요금제 변경 이의 접수됨</strong> — {data.pending.memberSize} 요금제로의 변경을 멈추고 확인 중입니다. 담당자가
                연락드립니다.
              </>
            )}
          </div>
        )}

        <div className="manage-stats" style={{ margin: 0 }}>
          <div className="manage-stat-card">
            <span>요금제 성도 규모</span>
            <strong>{data.memberSize ?? '미설정'}</strong>
          </div>
          <div className="manage-stat-card">
            <span>최근 {rules.activeWindowDays}일 사용 기기</span>
            <strong>
              {data.activeDevices.toLocaleString()}대
              {data.memberLimit != null && (
                <small style={{ fontSize: 13, fontWeight: 500, marginLeft: 6 }}>/ {data.memberLimit.toLocaleString()}</small>
              )}
            </strong>
          </div>
          <div className="manage-stat-card">
            <span>교적 재적 인원 (참고)</span>
            <strong>{data.rollMembers.toLocaleString()}명</strong>
          </div>
          {data.plan && (
            <div className="manage-stat-card">
              <span>월 요금 · 다음 결제일</span>
              <strong style={{ fontSize: 17 }}>
                {won(data.plan.monthlyFee)}
                <small style={{ display: 'block', fontSize: 13, fontWeight: 500 }}>
                  {data.plan.autoRenew ? koreanDate(data.plan.nextBillingDate) : '정기결제 미등록'}
                </small>
              </strong>
            </div>
          )}
        </div>

        {ratio != null && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
              <span>한도 사용률</span>
              <strong>{Math.round(ratio * 100)}%</strong>
            </div>
            <div style={{ height: 8, borderRadius: 999, background: '#eef1f6', overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(100, ratio * 100)}%`, height: '100%', background: BAR_COLOR[data.level] }} />
            </div>
          </div>
        )}

        {data.history.length > 0 && (
          <div>
            <div style={{ fontSize: 13, marginBottom: 6 }}>월별 사용 기기</div>
            <div className="manage-mini-chart">
              {data.history.map((h) => (
                <div className="manage-mini-chart-col" key={h.month}>
                  <div
                    className="manage-mini-chart-bar"
                    style={{
                      height: `${Math.max(4, (h.activeDevices / historyMax) * 100)}%`,
                      background: h.memberLimit != null && h.activeDevices > h.memberLimit ? 'var(--danger)' : undefined,
                    }}
                    title={`${h.activeDevices}대${h.memberLimit != null ? ` / 한도 ${h.memberLimit}` : ''}`}
                  />
                  <span className="manage-mini-chart-label">{Number(h.month.split('-')[1])}월</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="manage-panel-desc" style={{ margin: 0, fontSize: 12 }}>
          사용 인원은 최근 {rules.activeWindowDays}일 동안 성도 앱을 연 기기 수입니다(한 사람이 기기 두 대를 쓰면 2대로 셉니다).{' '}
          {data.limitExempt
            ? '이 교회는 자동 요금제 변경 대상에서 제외되어 있습니다.'
            : `${rules.overMonths}개월 연속 한도의 ${Math.round(rules.overRatio * 100)}%를 넘으면 ${rules.noticeDays}일 이상 미리 알려드린 뒤 다음 결제일부터 상위 요금제가 적용됩니다.`}
        </p>
      </div>

      {objectionOpen && (
        <div className="manage-modal-overlay" onClick={() => setObjectionOpen(false)}>
          <div className="manage-modal" onClick={(e) => e.stopPropagation()}>
            <div className="manage-modal-header">
              <h3>요금제 변경 이의 제기</h3>
              <button type="button" className="manage-modal-close" onClick={() => setObjectionOpen(false)}>×</button>
            </div>
            <div className="manage-modal-body">
              <p className="manage-modal-desc">
                이의를 보내면 변경 적용이 멈추고, 교회앱모아 담당자가 확인 후 연락드립니다. 행사로 방문자가 일시적으로 많았던 경우처럼
                사유를 적어 주세요.
              </p>
              <div className="manage-form">
                <label className="manage-field">
                  <span>사유</span>
                  <textarea
                    rows={4}
                    value={objectionNote}
                    onChange={(e) => setObjectionNote(e.target.value)}
                    placeholder="예: 9~10월 부흥회로 방문자가 많았습니다. 11월부터는 평소 인원으로 돌아옵니다."
                  />
                </label>
                {objectionMessage && <span className="manage-inline-message">{objectionMessage}</span>}
              </div>
            </div>
            <div className="manage-modal-footer">
              <button type="button" className="manage-btn" onClick={() => setObjectionOpen(false)}>취소</button>
              <button type="button" className="manage-btn manage-btn--primary" onClick={sendObjection} disabled={sending}>
                {sending ? '보내는 중...' : '이의 보내기'}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
